import React, { useState } from 'react';
import { validateActivityPayload, demoVinculaPayload, GAME_MODE_CATALOG, type VinculaPayload } from '@interactive-room';
import { StudioLiveShell } from '../components/StudioLiveShell';
import { createDraftId, saveActivityDraft, type ActivityDraft } from '../activityStorage';
import { resolveStudioLaunchDefaults } from '../studioLaunch';
import { generateSubjectAwareActivityDraft } from '../studioActivityGeneration';
import type { StudioLiveEditorProps } from '../types/editor.types';

export function VinculaLiveEditor({
  draftId,
  embedded,
  onSaved,
  launchSubjectId,
  launchPresetId,
}: StudioLiveEditorProps) {
  const launch = resolveStudioLaunchDefaults(demoVinculaPayload, {
    draftId,
    launchSubjectId,
    launchPresetId,
  });
  const [payload, setPayload] = useState<VinculaPayload>(() => launch.payload);
  const [subjectTag, setSubjectTag] = useState(() => launch.subjectTag);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedId] = useState(draftId ?? createDraftId());

  const meta = GAME_MODE_CATALOG.vincula;
  const pairs = payload.pairs;

  const updatePair = (index: number, field: 'ladoA' | 'ladoB', value: string) => {
    setPayload((prev) => ({
      ...prev,
      pairs: prev.pairs.map((pair, i) => (i === index ? { ...pair, [field]: value } : pair)),
    }));
  };

  const addPair = () => {
    setPayload((prev) => ({
      ...prev,
      pairs: [...prev.pairs, { id: `p${prev.pairs.length + 1}`, ladoA: '', ladoB: '' }],
    }));
  };

  const removePair = (index: number) => {
    setPayload((prev) => ({
      ...prev,
      pairs: prev.pairs.filter((_, i) => i !== index),
    }));
  };

  const save = () => {
    try {
      validateActivityPayload('vincula', payload);
      setError(null);
      onSaved?.(
        saveActivityDraft({
          id: savedId,
          mode: 'vincula',
          title: payload.title,
          subjectTag: subjectTag.trim() || 'general',
          payload,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  };

  const handleAiBuild = async (prompt: string) => {
    setAiLoading(true);
    setError(null);
    setNotice(null);
    try {
      const { draft, notice: aiNotice } = await generateSubjectAwareActivityDraft({
        mode: 'vincula',
        subjectId: launchSubjectId,
        subjectTag: subjectTag.trim() || undefined,
        tuning: prompt,
        draftId: savedId,
        persist: false,
      });
      setPayload(draft.payload as VinculaPayload);
      if (draft.subjectTag) setSubjectTag(draft.subjectTag);
      if (aiNotice) setNotice(aiNotice);
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'subject_context_required'
          ? 'Selecciona una asignatura desde Herramientas para generar con IA.'
          : e instanceof Error
            ? e.message
            : 'No se pudo generar con IA',
      );
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <StudioLiveShell
      embedded={embedded}
      error={error}
      subjectTag={subjectTag}
      onSubjectTagChange={setSubjectTag}
      onSave={save}
      aiActivityLabel="emparejamiento"
      onAiBuild={handleAiBuild}
      aiLoading={aiLoading}
      sidebarTitle="Emparejar"
      sidebarMinimal
    >
      {notice ? (
        <p className="mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-900">
          {notice}
        </p>
      ) : null}
      <header className="plugin-live-toolbar plugin-live-toolbar--ghost">
        <input
          className="canvas-inline-input canvas-inline-input--title min-w-0 flex-1 bg-transparent font-display text-base font-semibold tracking-tight"
          value={payload.title}
          onChange={(e) => setPayload((prev) => ({ ...prev, title: e.target.value }))}
          placeholder="Título de la actividad"
        />
        <span className="studio-pill">{meta.displayName}</span>
      </header>

      <div className="studio-canvas min-h-0 flex-1 overflow-y-auto p-4">
        <p className="mb-4 text-sm text-[var(--sala-ink-dim)]">
          Columna A enfrentada a columna B — los alumnos unen cada par en la sala.
        </p>
        <div className="space-y-3">
          {pairs.map((pair, index) => (
            <div key={pair.id || index} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <input
                className="sala-input-dark text-sm"
                value={pair.ladoA}
                placeholder="Columna A"
                onChange={(e) => updatePair(index, 'ladoA', e.target.value)}
              />
              <input
                className="sala-input-dark text-sm"
                value={pair.ladoB}
                placeholder="Columna B"
                onChange={(e) => updatePair(index, 'ladoB', e.target.value)}
              />
              <button
                type="button"
                className="text-xs text-red-400"
                onClick={() => removePair(index)}
                aria-label="Eliminar par"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="sala-btn-outline mt-4 text-xs" onClick={addPair}>
          + Par
        </button>
      </div>
    </StudioLiveShell>
  );
}
